import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HostnameService } from '@services/hostname.service';
import { MatDialog } from '@angular/material/dialog';
import { GlobalDataService } from '@services/global-data.service';
import { Router } from '@angular/router';
import { MakersiteService } from '@services/makersite.service';
import { ToastrService } from '@services/toaster.service';
import { LocalStorageService } from '@services/local-storage.service';
import { map } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';
import { InviteShareComponent } from '@dialogs/share-invite/share-invite-dialog.component';
import { EnrichProductComponent } from '@dialogs/enrich-product/enrich-product.component';
import { cleanupContextualTools } from '@utils/contextual-tool-cleanup';

@Injectable()
export class MattersmapService {
  baseUrl: string;
  private globalDataService = inject(GlobalDataService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private makersiteService = inject(MakersiteService);
  private toastrService = inject(ToastrService);
  private localStorageService = inject(LocalStorageService);

  constructor(
    private httpClient: HttpClient,
    private hostnameService: HostnameService
  ) {
    this.baseUrl = this.hostnameService.getOrigin();
  }

  getTileCards(rid) {
    rid = rid.replace('#', '');
    return this.httpClient.get(`${this.baseUrl}/api/v1/cards/rids?rids=${rid}`);
  }

  getModuleTools(rid: string) {
    return this.httpClient.get(`${this.baseUrl}/api/v1/info/module/composer?rid=${rid}&is_new_formschema=true`).pipe(
      map((response: any) => {
        if (response?.contextualTools?.length) {
          response.contextualTools = cleanupContextualTools(response.contextualTools);
        }
        return response;
      })
    );
  }

  getSidePanelDetailsWithSubModule(rid: string, layer: string, module: string, submodule: string) {
    const query = new URLSearchParams({ module, submodule, layer });
    return this.httpClient.get(`${this.baseUrl}/api/v1/info/${rid}?${query}&is_new_formschema=true`).pipe(
      map((res: any) => {
        if (res.message) {
          return { contextualTools: [], chipContent: [], modelTools: [] };
        }
        if (res?.contextualTools?.length) {
          res.contextualTools = cleanupContextualTools(res.contextualTools);
        }
        return res;
      })
    );
  }

  resumeImport(resumeBomImport: any) {
    this.globalDataService.setResumeImport(resumeBomImport);
    this.router.navigate(['/', 'importer', 'bom_import']);
  }

  getEnrichProduct(rid: string) {
    const data = {
      multiple: true,
      selectOption: false,
      type: 'enrich-search',
      action: 'enrich-product-search',
      rid
    };

    const dialogRef = this.dialog.open(EnrichProductComponent, {
      data,
      height: '70vh',
      width: '50vw'
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.makersiteService.enrichProduct(rid, result).subscribe((res: any) => {
          this.toastrService.showMessage(res ? res.message : 'Something went wrong', 3000);
        });
      }
    });
  }

  openDatasheet(rid: string) {
    this.globalDataService.setSelectedApp(undefined);
    this.globalDataService.setPpmSelectedItem(undefined);
    this.router.navigate(['/mattermaps/overview/', rid]);
  }

  dataOperation(action: 'duplicate' | 'copy' | 'merge' | 'deploy' | 'fork', rid) {
    if (action === 'duplicate') action = 'copy';
    this.makersiteService.dataSetOprations(action, rid).subscribe((res: any) => {
      this.toastrService.showMessage('Success', 3000);
      this.router.navigate(['/mattermaps/overview/', res.rid.replace('#', ''), 'edit']);
    });
  }

  pull(rid) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Publish to Makersite',
        text: null,
        cancel_text: 'Cancel',
        publish_text: 'Publish',
        type: 'Addpullrequest'
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const obj = {
          key: rid,
          comment: result
        };
        if (rid && obj.comment !== undefined) {
          this.makersiteService.dataSetOprations('pull', rid, obj).subscribe((res: any) => {
            this.toastrService.showMessage(res.message, 2000);
            this.router.navigateByUrl('/dashboard', { skipLocationChange: true }).then(() => {
              // NOTE: 0ms setTimeout required to fix run order issue where the menuService.selectedAction$ hadnt been cleared before the mattermaps-edit reloaded, causing the pull request dialog to open again
              setTimeout(() => {
                this.router.navigate(['/mattermaps/overview/', rid, 'edit']);
              }, 0);
            });
          });
        }
      }
    });
  }

  openInMcda(rid) {
    this.router.navigate(['mcda', rid]);
  }

  reCalculate(selectedTabName, rid) {
    const tab = selectedTabName;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: `Re-calculate ${tab}`,
        text: `Re-calculating will replace all existing ${tab}. Are you sure?`,
        cancel_text: 'No',
        delete_text: 'Yes'
      }
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (!confirm) return;
      if (tab === 'Environmental Impacts') {
        this.makersiteService.reCalculateEI(rid).subscribe((res: any) => {
          this.toastrService.showMessage(res.message);
          // this.reloadData();
        });
      } else if (tab === 'Exemptions') {
        this.makersiteService.reCalculateExemptions(rid).subscribe((res: any) => {
          this.toastrService.showMessage(res.message);
          // this.reloadData();
        });
      }
    });
  }

  deleteData(productName, rid) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Delete Item?',
        text: `Would you like to delete the ${productName} dataset?`,
        cancel_text: 'Cancel',
        delete_text: 'Delete'
      }
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (!confirm) return;
      this.makersiteService.deleteData(rid).subscribe((res) => {
        const trash = Object.values(res)[0];
        const trashIds = trash.trash_rids;
        if (trash.message === 'success') {
          this.toastrService
            .showWithAction('Deleted Successfully', 'Undo', 5000)
            .afterDismissed()
            .subscribe((restore) => {
              if (!restore.dismissedByAction) return;
              this.makersiteService.restoreItem(trashIds[0]).subscribe((response: any) => {
                this.toastrService.showMessage('Restored successfully.');
                const temp = response.message.split(' ');
                this.router.navigate(['mattermaps/overview/', temp[1], 'edit']);
              });
            });

          this.router.navigate(['dashboard']);
        }
      });
    });
  }

  convertNormalTextToUrl(url: string) {
    return url.trim().toLowerCase().replace(' ', '-');
  }

  inviteDialogNew() {
    this.dialog.open(InviteShareComponent, {
      data: { type: 'showInvite', value: 'Invite' },
      panelClass: 'show-invite'
    });
  }

  shareDialogNew(layer: string, rid: string) {
    const userInfo = this.localStorageService.getItem('user_info');
    const link = `/mattermaps/${layer}/${rid}`;
    const shareData = {
      type: 'showInvite',
      value: 'Share',
      data: {
        fr: userInfo.email,
        link,
        rid,
        usersLackingAccessList: ''
      }
    };
    this.dialog.open(InviteShareComponent, {
      data: shareData,
      panelClass: 'show-invite'
    });
  }
}
